import React, { useEffect, useState } from "react";
import {
  IonContent,
  IonPage,
  IonInput,
  IonButton,
  IonText,
  IonSpinner,
} from "@ionic/react";
import { useIonRouter } from "@ionic/react";
import { useVerifyRegistrationCode, useSendRegistrationCode } from "@/hooks";
import { getErrorMessage } from "@/api/client";
import { registrationStorage } from "@/services/storage";
import "./Auth.css";
import AppLogo from "@components/AppLogo.tsx";

// Default code configuration (will be updated from API response)
const DEFAULT_CODE_CHARACTERS = "BCDFGHJKLMNPQRSTVWXYZ";
const DEFAULT_CODE_LENGTH = 6;

const VerifyEmail: React.FC = () => {
  const router = useIonRouter();
  const [email, setEmail] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [codeCharacters, setCodeCharacters] = useState<string>(
    DEFAULT_CODE_CHARACTERS
  );
  const [codeLength, setCodeLength] = useState<number>(DEFAULT_CODE_LENGTH);
  const verifyMutation = useVerifyRegistrationCode();
  const resendMutation = useSendRegistrationCode();

  useEffect(() => {
    // Load email from storage
    registrationStorage.getEmail().then((storedEmail) => {
      if (storedEmail) {
        setEmail(storedEmail);
      } else {
        // No email stored, go back to welcome
        router.push("/auth/welcome", "back", "replace");
      }
    });
  }, [router]);

  const handleCodeChange = (value: string | null | undefined) => {
    const regex = new RegExp(`[^${codeCharacters}]`, "gi");
    const cleaned = (value || "")
      .toUpperCase()
      .replace(regex, "")
      .slice(0, codeLength);
    setCode(cleaned);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== codeLength || !email) return;

    try {
      await verifyMutation.mutateAsync({ email, code });
      // Code is valid, store it for onboarding and navigate
      sessionStorage.setItem("registration_code", code);
      router.push("/auth/onboarding", "forward");
    } catch {
      // Error handled by mutation
    }
  };

  const handleResend = async () => {
    if (!email) return;
    try {
      const response = await resendMutation.mutateAsync(email);
      // Update code configuration from response
      if (response.code_characters) {
        setCodeCharacters(response.code_characters);
      }
      if (response.code_length) {
        setCodeLength(response.code_length);
      }
    } catch {
      // Error handled by mutation
    }
  };

  const handleBack = () => {
    router.push("/auth/welcome", "back");
  };

  return (
    <IonPage>
      <IonContent className="auth-content ion-padding">
        <div className="auth-container">
          <div className="auth-header">
            <AppLogo />
            <IonText color="dark">
              <h1>Verificatie e-mail</h1>
              <p>
                We hebben een verificatiecode gestuurd naar
                <br />
                <strong>{email}</strong>
              </p>
            </IonText>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <IonInput
              className="code-input"
              style={{
                width: "100%",
                maxWidth: "200px",
                margin: "0 auto 24px",
                display: "block",
                textTransform: "uppercase",
              }}
              type="text"
              placeholder={"X".repeat(codeLength)}
              value={code}
              maxlength={codeLength}
              onIonInput={(e) => handleCodeChange(e.detail.value)}
            />

            {verifyMutation.error && (
              <IonText color="danger" className="error-text">
                <p>{getErrorMessage(verifyMutation.error)}</p>
              </IonText>
            )}

            <IonButton
              expand="block"
              type="submit"
              className="auth-button"
              disabled={code.length !== codeLength || verifyMutation.isPending}
            >
              {verifyMutation.isPending ? (
                <>
                  <IonSpinner name="crescent" />
                  <span style={{ marginLeft: "8px" }}>Verifi&euml;ren...</span>
                </>
              ) : (
                "Verifieer"
              )}
            </IonButton>

            <div className="auth-link">
              <IonButton
                fill="clear"
                size="small"
                onClick={handleResend}
                disabled={resendMutation.isPending}
              >
                {resendMutation.isPending
                  ? "Code versturen..."
                  : "Code opnieuw versturen"}
              </IonButton>
            </div>

            {resendMutation.isSuccess && (
              <IonText color="success" className="error-text">
                <p style={{ textAlign: "center" }}>Nieuwe code verstuurd!</p>
              </IonText>
            )}

            <div className="auth-link">
              <IonButton fill="clear" size="small" onClick={handleBack}>
                Terug
              </IonButton>
            </div>
          </form>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default VerifyEmail;
